import React, { useState, useEffect } from 'react'
import { makeApiCall } from '../utils/customerUtils'
import { SettingsPageTemplate, SettingsCard, InfoTip } from '../components/SettingsPageTemplate'
import { SearchIcon, PhoneIcon, ShoppingCartIcon } from '../components/Icons'

interface AvailableNumber {
  phone_number: string
  phone_number_type: 'local' | 'toll-free' | 'international'
  country_code: string
  region: string
  locality: string
  features: Array<'voice' | 'sms' | 'mms' | 'fax'>
  setup_price: string
  monthly_price: string
}

interface UserPhoneNumber {
  id: number
  phone_number: string
  status: 'available' | 'assigned' | 'suspended' | 'porting'
  number_type: 'local' | 'toll-free' | 'international'
  monthly_cost?: number
  setup_cost?: number
  assigned_at?: string
  features: Array<'voice' | 'sms' | 'mms' | 'fax'>
}

interface SearchParams {
  country_code: string
  phone_number_type: 'local' | 'toll-free'
  area_code: string
  locality: string
}

const PhoneNumberSettings: React.FC = () => {
  const [userPhoneNumber, setUserPhoneNumber] = useState<UserPhoneNumber | null>(null)
  const [availableNumbers, setAvailableNumbers] = useState<AvailableNumber[]>([])
  const [searchParams, setSearchParams] = useState<SearchParams>({
    country_code: 'US',
    phone_number_type: 'local',
    area_code: '',
    locality: ''
  })
  const [loading, setLoading] = useState(true)
  const [searching, setSearching] = useState(false)
  const [purchasing, setPurchasing] = useState<string | null>(null)
  const [releasing, setReleasing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    fetchUserPhoneNumber()
  }, [])

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null)
        setError(null)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [success, error])

  const fetchUserPhoneNumber = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await makeApiCall('api/phone-numbers/my-number', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch phone number')
      }

      const data = await response.json()
      setUserPhoneNumber(data.phone_number)

    } catch (err) {
      console.error('Error fetching phone number:', err)
      // Don't show error for no phone number (404 is expected)
      if ((err as any)?.message !== 'Failed to fetch phone number') {
        setError(err instanceof Error ? err.message : 'Failed to load phone number')
      }
    } finally {
      setLoading(false)
    }
  }

  const searchAvailableNumbers = async () => {
    try {
      setSearching(true)
      setError(null)
      setAvailableNumbers([])

      const queryParams = new URLSearchParams({
        country_code: searchParams.country_code,
        phone_number_type: searchParams.phone_number_type,
        limit: '10'
      })

      if (searchParams.area_code) {
        queryParams.append('area_code', searchParams.area_code)
      }
      if (searchParams.locality) {
        queryParams.append('locality', searchParams.locality)
      }

      const response = await makeApiCall(`api/phone-numbers/search?${queryParams}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (!response.ok) {
        throw new Error('Failed to search phone numbers')
      }

      const data = await response.json()
      setAvailableNumbers(data.numbers || [])

      if (data.numbers?.length === 0) {
        setError('No phone numbers available for the selected criteria. Try different search parameters.')
      }

    } catch (err) {
      console.error('Error searching phone numbers:', err)
      setError(err instanceof Error ? err.message : 'Failed to search phone numbers')
    } finally {
      setSearching(false)
    }
  }

  const purchasePhoneNumber = async (phoneNumber: string) => {
    try {
      setPurchasing(phoneNumber)
      setError(null)
      setSuccess(null)

      const response = await makeApiCall('api/phone-numbers/purchase', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          phone_number: phoneNumber
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to purchase phone number')
      }

      setSuccess(`✅ Phone number ${phoneNumber} purchased successfully! You can now use it for calls and SMS.`)
      
      // Refresh user phone number
      await fetchUserPhoneNumber()
      
      // Clear search results
      setAvailableNumbers([])

    } catch (err) {
      console.error('Error purchasing phone number:', err)
      setError(err instanceof Error ? err.message : 'Failed to purchase phone number')
    } finally {
      setPurchasing(null)
    }
  }

  const releasePhoneNumber = async () => {
    if (!userPhoneNumber) return

    try {
      setReleasing(true)
      setError(null)
      setSuccess(null)

      const response = await makeApiCall('api/phone-numbers/my-number', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to release phone number')
      }

      setSuccess(`✅ Phone number ${userPhoneNumber.phone_number} released successfully.`)
      setUserPhoneNumber(null)

    } catch (err) {
      console.error('Error releasing phone number:', err)
      setError(err instanceof Error ? err.message : 'Failed to release phone number')
    } finally {
      setReleasing(false)
    }
  }

  const formatPrice = (price: string | number) => {
    if (!price) return 'N/A'
    const num = typeof price === 'string' ? parseFloat(price) : price
    return isNaN(num) ? 'N/A' : `$${num.toFixed(2)}`
  }

  const formatFeatures = (features: any[]) => {
    if (!features || !Array.isArray(features)) return 'N/A'
    
    // Handle both string arrays and object arrays
    const featureStrings = features.map(feature => {
      if (typeof feature === 'string') {
        return feature
      } else if (typeof feature === 'object' && feature.name) {
        return feature.name
      } else if (typeof feature === 'object' && feature.type) {
        return feature.type
      } else {
        return String(feature)
      }
    })
    
    return featureStrings.join(', ') || 'N/A'
  }

  const messageState = error ? { type: 'error' as const, text: error } : 
                       success ? { type: 'success' as const, text: success } : null

  return (
    <SettingsPageTemplate
      title="Phone Number Management"
      description="Purchase and manage your dedicated phone number for calls and SMS"
      message={messageState}
      loading={loading}
    >
      <div className="space-y-6">
        
        {/* Current Phone Number */}
        <SettingsCard title="📱 Your Phone Number">
          {userPhoneNumber ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-green-800 dark:text-green-200 text-lg">
                      {userPhoneNumber.phone_number}
                    </h3>
                    <p className="text-green-700 dark:text-green-300 text-sm">
                      Type: {userPhoneNumber.number_type} • 
                      Features: {userPhoneNumber.features.join(', ')}
                    </p>
                    {userPhoneNumber.assigned_at && (
                      <p className="text-green-600 dark:text-green-400 text-xs mt-1">
                        Assigned: {new Date(userPhoneNumber.assigned_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <PhoneIcon className="text-green-600" size={24} />
                  </div>
                </div>
              </div>

              <InfoTip type="success">
                <strong>Your phone number is active!</strong><br/>
                You can now make and receive calls, send and receive SMS messages using this number.
                This number will appear as your caller ID for outbound calls and SMS.
              </InfoTip>

              <div className="flex justify-end">
                <button
                  onClick={releasePhoneNumber}
                  disabled={releasing}
                  className="px-4 py-2 text-red-700 bg-red-100 rounded-md hover:bg-red-200 dark:bg-red-900/20 dark:text-red-300 dark:hover:bg-red-900/40 transition-colors disabled:opacity-50"
                >
                  {releasing ? 'Releasing...' : 'Release Number'}
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <PhoneIcon className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Phone Number Assigned
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                You don't have a dedicated phone number yet. Purchase one below to enable calls and SMS.
              </p>
            </div>
          )}
        </SettingsCard>

        {/* Search and Purchase - only show if user doesn't have a number */}
        {!userPhoneNumber && (
          <>
            <SettingsCard title="🔍 Search Available Numbers">
              <div className="space-y-4">
                <InfoTip type="info">
                  Search for available phone numbers in your area. Local numbers are typically cheaper and 
                  provide better regional presence for your customers.
                </InfoTip>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Country
                    </label>
                    <select
                      value={searchParams.country_code}
                      onChange={(e) => setSearchParams(prev => ({ ...prev, country_code: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
                    >
                      <option value="US">United States</option>
                      <option value="CA">Canada</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Type
                    </label>
                    <select
                      value={searchParams.phone_number_type}
                      onChange={(e) => setSearchParams(prev => ({ ...prev, phone_number_type: e.target.value as 'local' | 'toll-free' }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
                    >
                      <option value="local">Local</option>
                      <option value="toll-free">Toll-Free</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Area Code (Optional)
                    </label>
                    <input
                      type="text"
                      value={searchParams.area_code}
                      onChange={(e) => setSearchParams(prev => ({ ...prev, area_code: e.target.value }))}
                      placeholder="e.g., 514"
                      maxLength={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      City (Optional)
                    </label>
                    <input
                      type="text"
                      value={searchParams.locality}
                      onChange={(e) => setSearchParams(prev => ({ ...prev, locality: e.target.value }))}
                      placeholder="e.g., Montreal"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
                    />
                  </div>
                </div>

                <div className="flex justify-center">
                  <button
                    onClick={searchAvailableNumbers}
                    disabled={searching}
                    className="flex items-center px-6 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 transition-colors disabled:opacity-50"
                  >
                    <SearchIcon className="mr-2" size={20} />
                    {searching ? 'Searching...' : 'Search Numbers'}
                  </button>
                </div>
              </div>
            </SettingsCard>

            {/* Available Numbers */}
            {availableNumbers.length > 0 && (
              <SettingsCard title="🛒 Available Numbers">
                <div className="space-y-3">
                  {availableNumbers.map((number) => (
                    <div
                      key={number.phone_number}
                      className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900 dark:text-white">
                          {number.phone_number}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {number.locality}, {number.region} • {number.phone_number_type}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-500">
                          Features: {formatFeatures(number.features)}
                        </div>
                      </div>
                      
                      <div className="text-right mr-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatPrice(number.setup_price)} setup
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          {formatPrice(number.monthly_price)}/month
                        </div>
                      </div>

                      <button
                        onClick={() => purchasePhoneNumber(number.phone_number)}
                        disabled={purchasing === number.phone_number}
                        className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors disabled:opacity-50"
                      >
                        <ShoppingCartIcon className="mr-1" size={16} />
                        {purchasing === number.phone_number ? 'Purchasing...' : 'Purchase'}
                      </button>
                    </div>
                  ))}
                </div>
              </SettingsCard>
            )}
          </>
        )}

        {/* Information */}
        <InfoTip type="info">
          <strong>💡 How Phone Numbers Work:</strong><br/>
          <br/>
          ✓ <strong>Dedicated Number:</strong> Your purchased number is exclusively yours for calls and SMS<br/>
          ✓ <strong>Caller ID:</strong> Your number appears when making outbound calls<br/>
          ✓ <strong>Two-Way Communication:</strong> Receive calls and SMS directly to your number<br/>
          ✓ <strong>Business Presence:</strong> Local numbers give you regional credibility<br/>
          <br/>
          💰 <strong>Billing:</strong> Monthly charges appear on your account. Setup fees are one-time only.
        </InfoTip>

      </div>
    </SettingsPageTemplate>
  )
}

export default PhoneNumberSettings
